// unmanaged
// "New project": pick the company (shows the ID it will issue), name it,
// and optionally set offering, lead and priority.
import React, { useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from "@mui/material";
import { createProject } from "./projectApi";
import { PRIORITY_LABEL } from "./projectParts";

const EMPTY = { company_id: "", name: "", offering: "", project_lead: "", priority: "medium" };

export default function NewProjectDialog({ open, meta, onClose, onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const company = (meta?.companies || []).find((c) => c.id === form.company_id);
  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const close = () => {
    setForm(EMPTY);
    setError("");
    onClose();
  };

  const create = async () => {
    setSaving(true);
    setError("");
    try {
      const body = { ...form, offering: form.offering || null, project_lead: form.project_lead || null };
      const created = await createProject(body);
      setForm(EMPTY);
      onCreated(created);
    } catch (e) {
      setError(e?.message || "Could not create the project");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>New project</DialogTitle>
      <DialogContent>
        <TextField
          select
          label="Company"
          value={form.company_id}
          onChange={set("company_id")}
          fullWidth
          margin="normal"
          required
          helperText={company ? `This project will be ${company.next_id}` : "Companies need a project prefix first (Companies page)"}
        >
          {(meta?.companies || []).map((c) => (
            <MenuItem key={c.id} value={c.id} disabled={!c.project_prefix}>
              {c.name || c.id}{c.project_prefix ? ` (${c.project_prefix})` : " — no prefix yet"}
            </MenuItem>
          ))}
        </TextField>
        <TextField label="Project name" value={form.name} onChange={set("name")} fullWidth margin="normal" required />
        <TextField select label="Offering" value={form.offering} onChange={set("offering")} fullWidth margin="normal">
          <MenuItem value=""><em>Not set</em></MenuItem>
          {(meta?.offerings || []).map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
        </TextField>
        <TextField select label="Project lead" value={form.project_lead} onChange={set("project_lead")} fullWidth margin="normal">
          <MenuItem value=""><em>Unassigned</em></MenuItem>
          {(meta?.team || []).map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
        </TextField>
        <TextField select label="Priority" value={form.priority} onChange={set("priority")} fullWidth margin="normal">
          {(meta?.priorities || []).map((p) => <MenuItem key={p} value={p}>{PRIORITY_LABEL[p] || p}</MenuItem>)}
        </TextField>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Cancel</Button>
        <Button variant="contained" onClick={create} disabled={saving || !company?.project_prefix || !form.name.trim()}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}
